import * as SwitchPrimitives from '@radix-ui/react-switch';
import { cva, type VariantProps } from 'class-variance-authority';
import * as React from 'react';

import { cn } from '@/lib/utils';
import Text from './text';

const switchVariants = cva(
    [
        'focus-visible:ring-ring',
        'focus-visible:ring-offset-background',
        'data-[state=checked]:bg-blue-500',
        'data-[state=unchecked]:bg-input',
        'peer',
        'inline-flex',
        'shrink-0',
        'cursor-pointer',
        'items-center',
        'rounded-full',
        'border-2',
        'border-transparent',
        'shadow-xs',
        'transition-colors',
        'focus-visible:outline-hidden',
        'focus-visible:ring-2',
        'focus-visible:ring-offset-2',
        'disabled:cursor-not-allowed',
        'disabled:opacity-50',
    ],
    {
        variants: {
            size: {
                default: 'h-5 w-9',
                sm: 'h-[18px] w-8',
            },
        },
        defaultVariants: {
            size: 'default',
        },
    },
);

const thumbVariants = cva(
    'bg-background pointer-events-none block rounded-full shadow-lg ring-0 transition-transform',
    {
        variants: {
            size: {
                default: 'h-4 w-4 data-[state=checked]:translate-x-4 data-[state=unchecked]:translate-x-0',
                sm: 'h-3 w-3 data-[state=checked]:translate-x-[14px] data-[state=unchecked]:translate-x-0',
            },
        },
        defaultVariants: {
            size: 'default',
        },
    },
);

export interface SwitchProps
    extends React.ComponentPropsWithoutRef<typeof SwitchPrimitives.Root>,
        VariantProps<typeof switchVariants> {
    label?: string;
    wrapperClassName?: string;
}

const Switch = React.forwardRef<React.ElementRef<typeof SwitchPrimitives.Root>, SwitchProps>(
    ({ className, size, label, wrapperClassName, ...props }, ref) => (
        <label className={cn('flex items-center gap-2 select-none cursor-pointer', wrapperClassName)}>
            <SwitchPrimitives.Root className={cn(switchVariants({ size, className }))} {...props} ref={ref}>
                <SwitchPrimitives.Thumb className={cn(thumbVariants({ size }))} />
            </SwitchPrimitives.Root>
            {label && <Text color="neutral-700">{label}</Text>}
        </label>
    ),
);
Switch.displayName = SwitchPrimitives.Root.displayName;

export { Switch };
